import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExecutionControls from '@features/scripts/components/execution/ExecutionControls';
import { useStationDevicesQuery } from '@api/bff/stationDevices';
import type { RunStartMode } from '@api/bff/runs/useRuns';
import type { StartRunBlockedReason } from '../hooks/useLaunchRun';

type ExecutionStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'FAILED'
  | 'ABORTED'
  | 'REJECTED';

type Props = {
  isRunActive: boolean;
  status: string | null;
  isDryRun: boolean;
  isConnected: boolean;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onAbort: () => void;
  following?: boolean;
  onPauseFollowing?: () => void;
  onResumeFollowing?: () => void;
  onStartRun?: () => void;
  onRehome?: () => void;
  startMode?: RunStartMode;
  onStartModeChange?: (mode: RunStartMode) => void;
  canStartRun?: boolean;
  startRunBlockedReason?: StartRunBlockedReason | null;
  isLaunching?: boolean;
  launchError?: string | null;
  onDismissLaunchError?: () => void;
  runFailure?: { status: 'FAILED' | 'REJECTED'; reason: string | null } | null;
  onDismissRunFailure?: () => void;
};

const statusChipColor = (
  status: string | null
): 'info' | 'warning' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'RUNNING':
      return 'info';
    case 'PAUSED':
      return 'warning';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
    case 'ABORTED':
      return 'error';
    default:
      return 'default';
  }
};

const RunHeader: React.FC<Props> = ({
  isRunActive,
  status,
  isDryRun,
  isConnected,
  onStart,
  onPause,
  onResume,
  onAbort,
  following,
  onPauseFollowing,
  onResumeFollowing,
  onStartRun,
  onRehome,
  startMode = 'Fresh',
  onStartModeChange,
  canStartRun = false,
  startRunBlockedReason = null,
  isLaunching = false,
  launchError = null,
  onDismissLaunchError,
  runFailure = null,
  onDismissRunFailure
}) => {
  const { t } = useTranslation('calibration');
  const { robots, activeStationId } = useStationDevicesQuery();
  const robot = robots.find(r => r.stationId === activeStationId) ?? robots[0];

  const blockedMessage =
    canStartRun || !startRunBlockedReason
      ? ''
      : {
          disconnected: t('runPage.startBlockedDisconnected', {
            defaultValue:
              'The robot connection is offline. Check the MQTT connection, then try again.'
          }),
          noStation: t('runPage.startBlockedNoStation', {
            defaultValue:
              'No robot station is configured. Add one in Settings before starting a run.'
          }),
          noAssignedCells: t('runPage.startBlockedNoAssignedCells', {
            defaultValue:
              'No instruments have scripts assigned. Select an instrument and assign a script to enable runs.'
          })
        }[startRunBlockedReason];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          ml: 'auto'
        }}
      >
        {robot && (
          <Chip
            icon={<RouterIcon />}
            label={robot.label || robot.stationId}
            size="small"
            color={isConnected ? 'success' : 'error'}
            variant="outlined"
          />
        )}
        {!isRunActive && onStartModeChange && (
          <Tooltip
            title={t('runPage.startModeTooltip', {
              defaultValue:
                'Fresh runs the station Master Setup first — powers the rig up and homes every axis (~4 min). Warm skips it, so only pick Warm when an earlier run this session finished normally and the arm is still homed. Master Teardown runs either way. Configure the sequences in Settings → Administration → Station Devices.'
            })}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={startMode}
              onChange={(_, value) =>
                value && onStartModeChange(value as RunStartMode)
              }
              aria-label={t('runPage.startModeLabel', {
                defaultValue: 'Robot start mode'
              })}
            >
              <ToggleButton value="Fresh">
                {t('runPage.startModeFresh', { defaultValue: 'Fresh' })}
              </ToggleButton>
              <ToggleButton value="Warm">
                {t('runPage.startModeWarm', { defaultValue: 'Warm' })}
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        )}
        {!isRunActive && onStartRun && (
          <Tooltip title={blockedMessage}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={onStartRun}
                disabled={!canStartRun || isLaunching}
              >
                {isLaunching
                  ? t('runPage.starting', { defaultValue: 'Starting…' })
                  : t('runPage.startRun', { defaultValue: 'Start run' })}
              </Button>
            </span>
          </Tooltip>
        )}
        {!isRunActive && onRehome && (
          <Tooltip
            title={t('runPage.rehomeTooltip', {
              defaultValue:
                'Power up and home the robot — recovery after an abort or power cycle'
            })}
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={onRehome}
                disabled={!isConnected}
              >
                {t('runPage.rehome', { defaultValue: 'Re-home' })}
              </Button>
            </span>
          </Tooltip>
        )}
        {isRunActive && (
        <>
          <Chip
            label={status ?? ''}
            size="small"
            color={statusChipColor(status)}
            variant="outlined"
          />
          {isDryRun && (
            <Chip
              label={t('runPage.dryRunBadge', { defaultValue: 'DRY RUN' })}
              size="small"
              color="warning"
            />
          )}
          {following !== undefined &&
            (following ? (
              <Chip
                icon={<GpsFixedIcon />}
                label={t('runPage.following', { defaultValue: 'Following' })}
                size="small"
                color="primary"
                onClick={onPauseFollowing}
                clickable
              />
            ) : (
              <Chip
                icon={<GpsNotFixedIcon />}
                label={t('runPage.resumeFollowing', {
                  defaultValue: 'Resume following'
                })}
                size="small"
                variant="outlined"
                onClick={onResumeFollowing}
                clickable
              />
            ))}
          <ExecutionControls
            status={(status as ExecutionStatus) ?? null}
            onStart={onStart}
            onPause={onPause}
            onResume={onResume}
            onAbort={onAbort}
            disabled={!isConnected}
          />
          </>
        )}
      </Box>
      {launchError && (
        <Alert
          severity="error"
          onClose={onDismissLaunchError}
          sx={{ width: '100%' }}
        >
          {launchError}
        </Alert>
      )}
      {runFailure && (
        <Alert
          severity="error"
          onClose={onDismissRunFailure}
          sx={{ width: '100%' }}
        >
          {runFailure.status === 'REJECTED'
            ? t('runPage.runRejectedBanner', {
                defaultValue: 'Run rejected: {{reason}}',
                reason:
                  runFailure.reason ??
                  t('runPage.runRejectedDefault', {
                    defaultValue: 'another run is still executing'
                  })
              })
            : t('runPage.runFailedBanner', {
                defaultValue: 'Run failed: {{reason}}',
                reason:
                  runFailure.reason ??
                  t('runPage.runFailedDefault', {
                    defaultValue: 'unknown error'
                  })
              })}
        </Alert>
      )}
    </>
  );
};

export default RunHeader;
